import { formatHours, type Metafield, type Task } from '@forecastify/shared';
import type { DataListColumn, DataListRow } from '@forecastify/ui';
import { metafieldCells, metafieldColumns } from '../metafield-columns';
import type { Translate } from '../../i18n';
import { initialsOf } from '../identity';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A task, as a row of the product's own list. One definition, two callers.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **EXTRACTED FOR C27.2 OUT OF `tasks-screen.tsx`, AND THE ALTERNATIVE WAS A SECOND TABLE.**
 * The agent's reply renders the tasks it read, and `docs/plan/agent.md` §4 is explicit about why it
 * renders them with **this** mapping: *"a second, chat-specific table would be a second place for
 * every column, colour and formatter to drift, and the drift would be invisible — the chat would
 * show `8.0` where the list shows `8:00`."*
 *
 * CLAUDE.md's frontend rule says the same thing from the other end: *"if a screen wants a
 * `TaskTable`, it wants the list component with task columns."* This file is the task columns.
 *
 * ⚠️ **It is not a component and takes no hooks.** `taskRows` needs the avatar map, which only a
 * component can fetch (`useAvatars`), so the caller passes it in — the same shape `tasks-screen.tsx`
 * already used, and what keeps this callable from a card, a tab or a screen.
 */

/**
 * Every column a task list can show, in the order `tasks-screen.tsx` shows them.
 *
 * ⚠️ **A closed set, so a caller cannot ask for a column with no cell behind it.** A `key` typo would
 * otherwise render an empty column with a translated header — a column that looks like data nobody
 * has.
 */
export const TASK_COLUMNS = [
  'key',
  'title',
  'project',
  'status',
  'priority',
  'assignee',
  'due',
  'estimate',
] as const;
/**
 * ⚠️ **Offered in the column chooser and OFF until ticked** (14 Oct 2026, ux-pass-2): the list the
 * mockup draws stays the default, and these are there for whoever wants them. Every one is already
 * on the task read — no second request.
 */
export const OPTIONAL_TASK_COLUMNS = [
  'lineItem',
  'startsOn',
  'tracked',
  'subtasks',
  'tags',
  /* J-1 · what the tracker files a synced task under. A typed task shows a dash in all three. */
  'components',
  'fixVersions',
  'epic',
] as const;

export type TaskColumnKey = (typeof TASK_COLUMNS)[number] | (typeof OPTIONAL_TASK_COLUMNS)[number];

/**
 * ⚠️⚠️ **THE CHAT'S FIVE, AND THE THREE IT DROPS ARE NAMED ON THE CARD.** §4's proposal: eight
 * columns do not fit the width a reply can take, and reducing a task to a name and one number is the
 * bulleted list with extra borders. Dropped: `project` (usually the filter that produced the list),
 * `priority` and `estimate`. `state-rules.md` §5's rule about a truncated total applies to a
 * truncated *shape* too — the card says what it left out rather than quietly showing less.
 */
export const CHAT_TASK_COLUMNS: readonly TaskColumnKey[] = [
  'key',
  'title',
  'status',
  'assignee',
  'due',
];

/**
 * The columns, translated.
 *
 * ⚠️ **The header keys are the EXISTING ones** — `project.tasks.column.status` and the rest — because
 * the chat renders the task list's columns and rule 7's catalogue should not grow a second set of
 * words for the same headings. `docs/plan/agent.md` §11 says so in as many words.
 *
 * ⚠️ **One `t()` per branch with a LITERAL key.** `t(keyFor(column))` is invisible to `i18n:check`,
 * which reads literal arguments only — every entry would report as an orphan and the fix somebody
 * reaches for is deleting it.
 */
export function taskColumns(
  t: Translate,
  keys: readonly TaskColumnKey[] = TASK_COLUMNS,
  /**
   * ⚠️ **Item 27 · the workspace's own `task` fields**, appended after the fixed ones. Optional, so
   * the agent's copy of this table (`agent-data-card.tsx`) renders without them — a chat bubble is
   * not the place for eleven columns.
   */
  metafields: readonly Metafield[] = [],
): DataListColumn[] {
  /* The optional ones ride along with the full list only — a chat card's five stay five. */
  const optional = keys === TASK_COLUMNS ? OPTIONAL_TASK_COLUMNS : [];
  return [
    ...keys.map(fixed(t)),
    ...optional.map((key) => ({ ...fixed(t)(key), defaultHidden: true })),
    ...metafieldColumns(metafields),
  ];
}

function fixed(t: Translate) {
  return (key: TaskColumnKey): DataListColumn => {
    /* ⚠️⚠️ **EVERY `sortKey` IS A NAME IN `TASK_SORT`, THE SERVER'S CATALOGUE** (item 45, D3). A
       key this list invents renders a header button that reorders nothing — the server drops what
       it does not recognise (`sorting.ts`), so the arrow would move and the rows would not. */
    if (key === 'key') {
      return { key, label: t('project.tasks.column.key'), width: 100, sortKey: 'key' };
    }
    if (key === 'title') {
      return { key, label: t('project.tasks.column.title'), sortKey: 'title' };
    }
    if (key === 'project') {
      return { key, label: t('task.field.project'), width: 160, sortKey: 'project' };
    }
    if (key === 'status') {
      /* ⚠️ Sorts on the workspace's own status ORDER, not the name — `TASK_SORT` says why. */
      return { key, label: t('project.tasks.column.status'), width: 150, sortKey: 'status' };
    }
    if (key === 'priority') {
      return { key, label: t('project.tasks.column.priority'), width: 130, sortKey: 'priority' };
    }
    if (key === 'assignee') {
      return { key, label: t('project.tasks.column.assignee'), width: 150, sortKey: 'assignee' };
    }
    if (key === 'due') {
      return { key, label: t('project.tasks.column.due'), width: 120, sortKey: 'due_on' };
    }
    if (key === 'lineItem') {
      return { key, label: t('task.field.lineItem'), width: 200 };
    }
    if (key === 'startsOn') {
      return { key, label: t('task.field.start'), width: 120, sortKey: 'starts_on' };
    }
    if (key === 'tracked') {
      return {
        key,
        label: t('task.column.tracked'),
        width: 100,
        align: 'right',
        sortKey: 'tracked_hours',
      };
    }
    if (key === 'subtasks') {
      return { key, label: t('task.column.subtasks'), width: 100, align: 'right' };
    }
    if (key === 'tags') {
      return { key, label: t('task.column.tags'), width: 180 };
    }
    /* ⚠️ No `sortKey` on the three: a list of names has no order the server could sort a task by. */
    if (key === 'components') {
      return { key, label: t('task.field.components'), width: 180 };
    }
    if (key === 'fixVersions') {
      return { key, label: t('task.field.fixVersions'), width: 140 };
    }
    if (key === 'epic') {
      return { key, label: t('task.field.epic'), width: 200 };
    }
    return {
      key,
      label: t('project.tasks.column.estimate'),
      width: 100,
      align: 'right',
      sortKey: 'estimate_hours',
    };
  };
}

/**
 * The rows.
 *
 * ⚠️ **Every cell for every column, whichever subset is being rendered.** `DataList` reads
 * `row.cells[column.key]` and ignores the rest, so building all eight costs nothing and means a
 * caller cannot produce a column with no cell. The subset is a `taskColumns` decision.
 *
 * ⚠️ **`formatHours`, never `String(estimateHours)`.** The wire carries `numeric` as a string
 * (`hard-won.md`'s driver rows), so a raw value renders `8.00` where the product says `8:00` — which
 * is exactly the drift this extraction exists to prevent.
 */
export function taskRows(
  t: Translate,
  tasks: readonly Task[],
  /** ⚠️ Path → object URL, from `useAvatars`. Absent is fine: the initials render underneath. */
  avatars: ReadonlyMap<string, string>,
  /** ⚠️ Item 27 · the definitions, so the cells and the columns cannot disagree about which show. */
  metafields: readonly Metafield[] = [],
): DataListRow[] {
  return tasks.map((task) => ({
    id: task.id,
    cells: {
      key: { text: task.key, mono: true, muted: true },
      title: { text: task.title, bold: true, sub: task.parentKey ?? undefined },
      project: {
        text: task.projectName ?? t('projects.internal'),
        muted: task.projectName === null,
      },
      /* J-2 · a status the tracker's map does not reach says so under it — the status is locked. */
      status: {
        dot: task.statusColor,
        text: task.statusName,
        ...(task.tracker?.unmappedStatus == null
          ? {}
          : { sub: t('task.status.unmapped', { status: task.tracker.unmappedStatus }) }),
      },
      priority: { dot: task.priorityColor, text: task.priorityName },
      assignee:
        task.assigneeName === null
          ? { text: '–', muted: true }
          : {
              /* ⚠️ C22 · the picture when there is one, the initials underneath when there is not
                 — `AvatarImage` keeps the fallback behind it either way. */
              avatar: {
                initials: initialsOf(task.assigneeName),
                ...(task.assigneeAvatarSeed === null ? {} : { seed: task.assigneeAvatarSeed }),
                title: task.assigneeName,
                src: avatars.get(task.assigneeAvatarUrl ?? ''),
              },
              text: task.assigneeName,
            },
      due: { text: task.dueOn ?? '–', mono: true, muted: true },
      estimate: { text: formatHours(task.estimateHours), mono: true },
      lineItem:
        task.lineItemName === null
          ? { text: '–', muted: true }
          : {
              text: task.lineItemName,
              ...(task.contractCode === null ? {} : { sub: task.contractCode }),
            },
      startsOn: { text: task.startsOn ?? '–', mono: true, muted: true },
      tracked: { text: formatHours(task.trackedHours), mono: true },
      subtasks:
        task.subtaskCount === 0
          ? { text: '–', muted: true }
          : { text: `${String(task.subtaskDoneCount)}/${String(task.subtaskCount)}`, mono: true },
      tags: task.tags.length === 0 ? { text: '–', muted: true } : { text: task.tags.join(', ') },
      components: listCell(task.tracker?.components ?? []),
      fixVersions: listCell(task.tracker?.fixVersions ?? []),
      epic:
        task.tracker?.epic == null ? { text: '–', muted: true } : { text: task.tracker.epic.name },
      /* ⚠️ **Item 27 · the workspace's own fields**, for every definition whose `inList` is on. */
      ...metafieldCells(t, metafields, task.metafields),
    },
  }));
}

/** A list of names as one cell, or the list's dash when there are none. */
function listCell(names: readonly string[]): DataListRow['cells'][string] {
  return names.length === 0 ? { text: '–', muted: true } : { text: names.join(', ') };
}
